import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IConfigLoadedSeriesData } from 'src/app/model/config-loaded-series-data';
import { isThingieSeries, ThingieSeries } from 'src/app/model/thingie-series';
import { IThingie } from 'src/app/model/thingie/thingie';
import { isScatterSeries } from 'src/app/model/values-config';
import {
  ExperimentSampleCsvBundlePayload,
  ExperimentSampleDataContext
} from 'src/app/services/chat/chat.service';
import { GraphConfigSeriesDataLoaderService } from 'src/app/services/data/series-data-loader/graph-config-series-data-loader.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { createDefaultGraphConfig } from 'src/app/utility/default-object-creator';
import { getThingieGroupId, getThingieReplicateIds } from 'src/app/utility/thingie-group-helper';
import { ThingieHelper } from 'src/app/utility/thingie-helper';

interface ISeriesSampleMetadata {
  seriesKey: string;
  seriesName: string;
  processIndex: number;
  taskIndex: number;
  taskLabel: string;
}

type IReplicateSampleBundle = ExperimentSampleCsvBundlePayload;

const CSV_HEADERS = [
  'replicate_name',
  'process_index',
  'task_index',
  'task_label',
  'series_key',
  'series_name',
  'point_index',
  'x',
  'y'
];

export const EXPERIMENT_SAMPLE_POINTS_PER_SERIES = 100;

export function buildExperimentSampleBundleKey(replicateThingieId: string): string {
  return `replicate:${replicateThingieId}:sample:${EXPERIMENT_SAMPLE_POINTS_PER_SERIES}`;
}

@Injectable({ providedIn: 'root' })
export class ExperimentSampleDatasetService {
  private static readonly MAX_BUNDLES = 12;
  private static readonly MAX_TOTAL_CSV_BYTES = 4_000_000;

  private readonly cache = new Map<string, Promise<ExperimentSampleDataContext | undefined>>();

  constructor(
    private readonly dataLoaderService: GraphConfigSeriesDataLoaderService,
    private readonly thingieService: ThingieService
  ) {}

  public async getOrBuildForThingie(thingie: IThingie): Promise<ExperimentSampleDataContext | undefined> {
    const replicateIds = getThingieReplicateIds(thingie);
    const experimentGroupId = getThingieGroupId(replicateIds);
    const cachedValue = this.cache.get(experimentGroupId);
    if (cachedValue) {
      return cachedValue;
    }

    const buildPromise = this.buildForExperiment(thingie, experimentGroupId)
      .catch((error: unknown) => {
        this.cache.delete(experimentGroupId);
        throw error;
      });

    this.cache.set(experimentGroupId, buildPromise);
    return buildPromise;
  }

  private async buildForExperiment(
    thingie: IThingie,
    experimentGroupId: string
  ): Promise<ExperimentSampleDataContext | undefined> {
    const replicateIds = getThingieReplicateIds(thingie);
    const replicateThingies = replicateIds.length > 1
      ? await firstValueFrom(this.thingieService.getThingiesByIds(replicateIds))
      : [thingie];
    const orderedReplicates = [...replicateThingies].sort(
      (left, right) => ThingieHelper.getReplicateFullname(left).localeCompare(ThingieHelper.getReplicateFullname(right))
    );

    const payloads: IReplicateSampleBundle[] = [];
    let totalCsvBytes = 0;
    let truncated = false;

    for (const replicateThingie of orderedReplicates) {
      if (payloads.length >= ExperimentSampleDatasetService.MAX_BUNDLES) {
        truncated = true;
        break;
      }

      const payload = await this.buildReplicateBundle(replicateThingie);
      if (!payload) {
        continue;
      }

      const nextTotalCsvBytes = totalCsvBytes + this.getByteLength(payload.csvContent);
      if (payloads.length > 0 && nextTotalCsvBytes > ExperimentSampleDatasetService.MAX_TOTAL_CSV_BYTES) {
        truncated = true;
        break;
      }

      totalCsvBytes = nextTotalCsvBytes;
      payloads.push(payload);
    }

    if (payloads.length === 0) {
      return undefined;
    }

    return {
      manifest: {
        experimentGroupId,
        experimentName: thingie.baseName,
        samplePointsPerSeries: EXPERIMENT_SAMPLE_POINTS_PER_SERIES,
        bundleCount: payloads.length,
        totalSeriesCount: payloads.reduce((sum, payload) => sum + payload.seriesCount, 0),
        totalRowCount: payloads.reduce((sum, payload) => sum + payload.rowCount, 0),
        bundles: payloads.map(payload => ({
          bundleKey: payload.bundleKey,
          filename: payload.filename,
          replicateThingieId: payload.replicateThingieId,
          replicateName: payload.replicateName,
          seriesCount: payload.seriesCount,
          taskCount: payload.taskCount,
          rowCount: payload.rowCount
        })),
        truncated: truncated ? true : undefined
      },
      payloads
    };
  }

  private async buildReplicateBundle(replicateThingie: IThingie): Promise<IReplicateSampleBundle | undefined> {
    const sampleableSeries = this.thingieService.getAvailableDataSeriesFromThingie(replicateThingie, false)
      .filter((series): series is ThingieSeries => isThingieSeries(series) && isScatterSeries(series));
    if (sampleableSeries.length === 0) {
      return undefined;
    }

    const requestedSeries = sampleableSeries.map(series => {
      const clonedSeries = structuredClone(series);
      clonedSeries.observed = true;
      return clonedSeries;
    });
    const graphConfig = createDefaultGraphConfig();
    graphConfig.thingie = replicateThingie._id;
    graphConfig.label = `Copilot experiment sample for ${ThingieHelper.getReplicateFullname(replicateThingie)}`;
    graphConfig.timeReference = 'ABSOLUTE';
    graphConfig.internalSeriesList = requestedSeries;

    const loadedSeries = await this.dataLoaderService
      .getDataLoader()
      .loadData(graphConfig, EXPERIMENT_SAMPLE_POINTS_PER_SERIES);

    const metadataBySeriesKey = new Map<string, ISeriesSampleMetadata>(
      sampleableSeries.map(series => {
        const process = replicateThingie.processes[series.identification.processIndex];
        const task = process?.tasks[series.identification.taskIndex];
        const seriesName = series.identification.dimensionName
          || task?.outputs?.[series.identification.output]?.name
          || series.identification.output;

        return [
          this.getSeriesKey(series),
          {
            seriesKey: this.getSeriesKey(series),
            seriesName,
            processIndex: series.identification.processIndex,
            taskIndex: series.identification.taskIndex,
            taskLabel: task?.name || series.identification.output
          }
        ];
      })
    );

    const rows: string[] = [CSV_HEADERS.join(',')];
    const taskKeys = new Set<string>();
    const replicateName = ThingieHelper.getReplicateFullname(replicateThingie);
    const bundleKey = buildExperimentSampleBundleKey(replicateThingie._id ?? '');

    for (const loadedData of loadedSeries) {
      if (loadedData.subseries === 'forecastParameters' || !isThingieSeries(loadedData.series)) {
        continue;
      }

      const metadata = metadataBySeriesKey.get(this.getSeriesKey(loadedData.series));
      if (!metadata) {
        continue;
      }

      taskKeys.add(`${metadata.processIndex}:${metadata.taskIndex}`);
      this.appendSeriesRows(rows, loadedData, metadata, replicateName);
    }

    const rowCount = rows.length - 1;
    if (rowCount <= 0) {
      return undefined;
    }

    return {
      bundleKey,
      filename: `experiment-sample-${this.slugify(replicateName)}.csv`,
      replicateThingieId: replicateThingie._id ?? '',
      replicateName,
      seriesCount: metadataBySeriesKey.size,
      taskCount: taskKeys.size,
      rowCount,
      csvContent: rows.join('\n')
    };
  }

  private appendSeriesRows(
    rows: string[],
    loadedData: IConfigLoadedSeriesData,
    metadata: ISeriesSampleMetadata,
    replicateName: string
  ): void {
    const pointCount = Math.max(loadedData.x.length, loadedData.y.length);

    for (let pointIndex = 0; pointIndex < pointCount; pointIndex += 1) {
      const xValue = this.stringifyCsvValue(loadedData.x[pointIndex]);
      const yValue = this.stringifyCsvValue(this.normalizePointValue(loadedData.y[pointIndex]));

      if (!xValue && !yValue) {
        continue;
      }

      rows.push([
        replicateName,
        metadata.processIndex.toString(),
        metadata.taskIndex.toString(),
        metadata.taskLabel,
        metadata.seriesKey,
        metadata.seriesName,
        pointIndex.toString(),
        xValue,
        yValue
      ].map(value => this.escapeCsvValue(value)).join(','));
    }
  }

  private normalizePointValue(value: unknown): unknown {
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return (value as { value?: unknown }).value;
    }

    return value;
  }

  private stringifyCsvValue(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private escapeCsvValue(value: string): string {
    const escapedValue = value.replace(/"/g, '""');
    if (/[",\n\r]/.test(escapedValue)) {
      return `"${escapedValue}"`;
    }

    return escapedValue;
  }

  private getByteLength(value: string): number {
    return new TextEncoder().encode(value).length;
  }

  private slugify(value: string): string {
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'replicate';
  }

  private getSeriesKey(series: ThingieSeries): string {
    return [
      'thingie',
      series.identification.thingieId ?? '',
      series.identification.processIndex,
      series.identification.taskIndex,
      series.identification.output,
      series.identification.offlineData ? 'offline' : 'live'
    ].join(':');
  }
}
